import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

import {
  ref,
  onMounted,
  onUnmounted,
  watch
} from 'vue';

// Components
import DiagGeneric from '@components/dialog/DiagGeneric.vue';

// Stores
import { useSecurityStore } from '@stores/security'

// Personal libs
import {
  convertSecondsInFinestTime,
  extractTimeDataFromSeconds,
} from '@shared/lib/date.lib';
import bus from '@shared/lib/eventbus.lib';
import { BusEventSessionEnum } from '@shared/enum/bus/bus-events-session.enum';
import { NotificationTypeEnum } from '@shared/enum/notification.enum';
import { closeAllModals, loggoutActions, redirectOnLoggout } from '@shared/lib/loggout.lib';
import { dbGetActiveUser } from '@data/datasource/local/user.database';
import {
  displayDialogError,
  displayNotifSuccess,
} from '@shared/lib/notify.lib';
import { sessionManager } from '@shared/service/sessionManager/session-manager.class';
import { prepareAuthPayload } from '@shared/lib/login.lib';
import { FormNameEnum } from '@shared/enum/form-name.enum';
import { InputFiedTypeEnum } from '@shared/enum/input.enum';
import { IDiagGenericResponse } from '@shared/interface/qDialogTemplate.interface';

/**
 * Reactive datas
 */

export default /*@__PURE__*/_defineComponent({
  __name: 'RefreshTokenComponent',
  setup(__props) {

// Vendor
const showConfirmation = ref<boolean>(false);
const showReconnect = ref<boolean>(false);
const showSessionExpired = ref<boolean>(false);
const password = ref<string | undefined>(undefined);
const refreshTokenTtl = ref<string | undefined>();

/**
 * Stores
 */
const securityStore = useSecurityStore()

/**
 * Methods
 */
function onCloseConfirmation() {
  showConfirmation.value = false;

  loggoutActions({
    from: 'RefreshTokenComponent - onCloseConfirmation',
    errorWhileRefreshToken: true,
  })
    .then(() => closeAllModals())
    .then(() => redirectOnLoggout());
}

function onValidConfirmation() {
  showConfirmation.value = false;
  showReconnect.value = true;

  sessionManager.askReconnect();
}

function onCloseLogin() {
  showReconnect.value = false;
  sessionManager.sessionInteractionStatus.value = undefined
}

function onCloseSessionExpired() {
  showSessionExpired.value = false
  closeAllModals()
  redirectOnLoggout()
}

async function onValidLogin(qDialogReturns: IDiagGenericResponse) {
  if (qDialogReturns.inputPassword != null) {
    const activeUser = await dbGetActiveUser();
    const email = activeUser?.email;

    const payload = await prepareAuthPayload({
      email,
      password: qDialogReturns.inputPassword,
      chkRemember: activeUser?.chkRemember,
      fingerprint: activeUser?.refreshToken?.fingerprint ?? (await securityStore.getFingerprint()) ?? '',
      csrf: (await securityStore.getCsrf(FormNameEnum.LOGIN))?.token
    })

    if (qDialogReturns.inputPassword != null && email != null) {
      sessionManager
        .auth(payload)
        .then(() => {
          displayNotifSuccess('Connexion réussie');
        })
        .catch((error: Error) =>
          displayDialogError({
            title: 'Erreur',
            message: error.message ?? 'null',
            isHtml: true,
          }),
        )
        .finally(() => {
          showReconnect.value = false;
        });
    } else {
      showReconnect.value = false;
    }
  } else {
    showReconnect.value = false;
  }
}

/**
 * Watch
 */
watch(() => sessionManager.isOnline.value, async (newVal: boolean | undefined, oldVal: boolean | undefined) => {
  if (newVal === true && oldVal === false) {
    const activeUser = await dbGetActiveUser()
    if (activeUser?.isConnected && !(await sessionManager.refreshToken.isValid())) {
      showConfirmation.value = false;
      showReconnect.value = false;
      showSessionExpired.value = false;

      sessionManager.askReconnect();
    }
  }
})

/**
 * Hooks
 */
onMounted(() => {
  bus.on(BusEventSessionEnum.ASKING_RECONNECT, () => {
    if (!showReconnect.value) {
      showConfirmation.value = false;
      showReconnect.value = true;
      showSessionExpired.value = false;
    }
  });

  bus.on(BusEventSessionEnum.ASKING_BEFORE_END, () => {
    if (!showConfirmation.value) {
      showConfirmation.value = true;
      showReconnect.value = false;
      showSessionExpired.value = false;
    }
  });

  bus.on(BusEventSessionEnum.ASKING_SESSION_EXPIRED, (rtTtl?: number) => {
    if (!showSessionExpired.value) {
      showConfirmation.value = false;
      showReconnect.value = false;
      showSessionExpired.value = true;

      refreshTokenTtl.value = convertSecondsInFinestTime(
        extractTimeDataFromSeconds(rtTtl ?? 0),
      );

      loggoutActions({
        from: 'RefreshTokenComponent - ASKING_SESSION_EXPIRED',
        errorWhileRefreshToken: true,
      });
    }
  });
});

onUnmounted(() => {
  bus.off(BusEventSessionEnum.ASKING_BEFORE_END);
  bus.off(BusEventSessionEnum.ASKING_SESSION_EXPIRED);
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(DiagGeneric, {
      show: showConfirmation.value,
      title: 'Information importante',
      message: `
        La session va expirer dans ${_unref(sessionManager).refreshToken.getRemainingTime()} secondes. Vous pouvez :<br>
        <ul>
        <li>soit vous déconnecter,</li>
        <li>soit prolonger la session (synonyme de reconnexion).</li>
        </ul>
    `,
      "notification-type": _unref(NotificationTypeEnum).WARNING,
      html: true,
      ok: {
        label: 'Prolonger la session',
      },
      cancel: {
        label: 'Se déconnecter',
      },
      onClose: _cache[0] || (_cache[0] = ($event: any) => (showConfirmation.value = false)),
      onCancel: _cache[1] || (_cache[1] = ($event: any) => (onCloseConfirmation())),
      onValid: _cache[2] || (_cache[2] = ($event: any) => (onValidConfirmation()))
    }, null, 8 /* PROPS */, ["show", "message", "notification-type"]),
    _createVNode(DiagGeneric, {
      show: showReconnect.value,
      title: 'Prolongation de la session',
      message: 'Pour prolonger la session, veuillez saisir de nouveau votre mot de passe.',
      "notification-type": _unref(NotificationTypeEnum).WARNING,
      html: false,
      "input-prompt": {
        model: password.value,
        type: _unref(InputFiedTypeEnum).PASSWORD,
        label: 'Mot de passe de connexion :',
        stackLabel: true,
        required: true,
      },
      "manage-loading": true,
      cancel: {
        label: 'Annuler',
      },
      ok: {
        label: 'Prolonger la session',
      },
      onClose: _cache[3] || (_cache[3] = ($event: any) => (onCloseLogin())),
      onCancel: _cache[4] || (_cache[4] = ($event: any) => (onCloseLogin())),
      onValid: onValidLogin
    }, null, 8 /* PROPS */, ["show", "notification-type", "input-prompt"]),
    _createVNode(DiagGeneric, {
      show: showSessionExpired.value,
      title: 'Information',
      message: `
        Votre session a expiré (durée de session : ${refreshTokenTtl.value}).<br>
        Pour continuer à utiliser l'application avec votre compte, vous devez vous reconnecter.
      `,
      "notification-type": _unref(NotificationTypeEnum).WARNING,
      html: true,
      "hide-cancel": true,
      ok: {
        label: 'Fermer',
      },
      onCancel: _cache[5] || (_cache[5] = ($event: any) => (onCloseSessionExpired())),
      onClose: _cache[6] || (_cache[6] = ($event: any) => (onCloseSessionExpired())),
      onValid: _cache[7] || (_cache[7] = ($event: any) => (showSessionExpired.value = false))
    }, null, 8 /* PROPS */, ["show", "message", "notification-type"])
  ]))
}
}

})